import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode, useContext } from "react";
import { createRoot } from "react-dom/client";
import { scan } from "react-scan";
import { Toaster } from "sonner";
import * as z from "zod";
import { AuthProvider } from "./auth";
import { ErrorComponent } from "./components/layout/ErrorComponent";
import { NotFound } from "./components/layout/NotFound";
import { StickyProvider } from "./components/layout/StickyContext";
import { ScreenSizeProvider } from "./providers/ScreenSizeProvider";
import ThemeProvider from "./providers/ThemeProvider";
import { ScrollElementContext } from "./providers/scroll-element";
import { ThemeContext } from "./providers/theme-context";
import { routeTree } from "./routeTree.gen";
import { trackPageView } from "./services/amplitude.service";
import "./styles/tailwind.css";
import { queryClient } from "./utils/api";

const enableReactScan = "never" as "always" | "dev" | "never";

z.config(z.locales.da());

if (import.meta.env.DEV && import.meta.env.VITE_SHOW_DEBUG_TOOLS !== "false") {
  scan({
    enabled:
      enableReactScan === "always" ||
      (enableReactScan === "dev" && import.meta.env.MODE === "development"),
    trackUnnecessaryRenders: true,
  });
}

const router = createRouter({
  routeTree,
  defaultPreload: undefined,
  unmaskOnReload: true,
  scrollRestoration: true,
  scrollToTopSelectors: ["#root"],
  getScrollRestorationKey: (location) => {
    const isolatedPaths = [/\/leads/, /\/campaigns\/\d+/];
    return isolatedPaths.some((regex) => regex.test(location.pathname))
      ? location.pathname
      : location.state.__TSR_key!;
  },
  defaultNotFoundComponent: (props) => (
    <NotFound {...props} data={props.data as Record<string, string>} />
  ),
  defaultOnCatch: (error, errorInfo) => {
    if (import.meta.env.DEV) {
      console.error(error, errorInfo); // * - Keep for stack trace in dev
    }
  },
  defaultPendingMinMs: 750,
  defaultPendingMs: 0, // Set to 0 to make routes pending immediately
  defaultErrorComponent: import.meta.env.DEV ? ErrorComponent : undefined,
  notFoundMode: "root",
  context: { queryClient },
});

type WithoutUndefinedProps<T> = {
  [K in keyof T as T[K] extends undefined ? never : K]: T[K];
};

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    sidebar?: React.FC;
    zoneMode?: boolean;
    playerActive?: boolean;
    playerBar?: React.FC;
    meta?: {
      title: string;
      isTitleRoot?: boolean;
    };
    isBreadcrumbRoot?: boolean;
    crumb?: string;
    historyStateSchema?: z.ZodType;
  }

  interface HistoryState {
    routeStates?: WithoutUndefinedProps<{
      [RouteId in keyof typeof router.routesById]?: (typeof router.routesById)[RouteId] extends {
        types: {
          routeContext: {
            parseHistoryState: (historyState: infer T) => unknown;
          };
        };
      }
        ? T
        : never;
    }>;
  }
}

Sentry.init({
  dsn: "https://fcc5f27e06897f3d5d039e4c1aebf440@o15265.ingest.us.sentry.io/4508873974874112",
  enabled: import.meta.env.PROD,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
    Sentry.tanstackRouterBrowserTracingIntegration(router),
  ],
  denyUrls: ["localhost"],
  environment: import.meta.env.MODE,
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/dev1\.api\.lassox\.com/,
    /^https:\/\/api\.lassox\.com/,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  // Before capture handler
});

function RouterProviderWithContext() {
  return <RouterProvider router={router} />;
}

// Separate content to make sure the ThemeProvider is rendered first
// and the theme is applied before the rest of the app, so that we can make toasts match the theme
function Content() {
  const { currentTheme } = useContext(ThemeContext);

  return (
    <>
      <ScreenSizeProvider>
        <Sentry.ErrorBoundary
          key={location.pathname}
          fallback={({ error, componentStack, resetError }) => (
            <ErrorComponent
              error={error as Error}
              reset={resetError}
              info={{ componentStack }}
            />
          )}
        >
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <RouterProviderWithContext />
            </AuthProvider>

            <Toaster
              theme={currentTheme}
              position="top-right"
              visibleToasts={3}
              offset={{
                top: 11,
                right: 27,
              }}
              toastOptions={{
                classNames: {
                  cancelButton: "text-white! bg-linear-(--theme-gradient)!",
                },
              }}
            />
            {/* <ReactQueryDevtools initialIsOpen={false} /> */}
          </QueryClientProvider>
        </Sentry.ErrorBoundary>
      </ScreenSizeProvider>
    </>
  );
}

const rootEl = document.getElementById("root")!;

export function App() {
  return (
    <ScrollElementContext.Provider value={rootEl}>
      <StickyProvider>
        <ThemeProvider>
          <Content />
        </ThemeProvider>
      </StickyProvider>
    </ScrollElementContext.Provider>
  );
}

createRoot(rootEl, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

router.subscribe("onResolved", (event) => {
  if (event.pathChanged) {
    trackPageView(event.toLocation.pathname, location.href);
  }
});
